import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import moment from "moment";
import dashboardServices from "app/services/dashboard-services";

// El panel de la tarjeta "Rutas iniciadas".
//
// Es el unico de los cinco que mira el presente, asi que es el unico que se refresca
// solo. Una fila por PEDIDO: el numero de viaje no le dice nada a nadie, y lo que se
// pregunta mirando esto es por que pedido va cada camion. Las filas del mismo viaje van
// juntas y solo la primera repite viaje, chofer y avance, que es lo que las deja leer
// como un grupo sin escribir el mismo dato ocho veces.
//
// El plan no siempre existe -solo los viajes que calcularon ruta tienen planned_end-,
// y cuando falta la fila lo dice en vez de quedarse vacia o inventar una estimacion.
const REFRESCO_MS = 60000;
const TONO = "#3E6B7A";

// La fecha se muestra solo cuando NO es la del panel. Sin esto una hora del dia
// siguiente se leia como si fuera de hoy: un viaje planificado para el 04 que el chofer
// arranco el 05 mostraba "12:04" al lado de "+1085 min" y el numero parecia un error,
// cuando lo que pasaba es que la hora era de otro dia.
const hora = (valor, dia) => {
    if (!valor) return null;

    const momento = moment(valor);
    return momento.isSame(moment(dia), "day")
        ? momento.format("HH:mm")
        : momento.format("DD-MM HH:mm");
};

const ActiveRoutes = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dia, setDia] = useState(moment().format("YYYY-MM-DD"));

    const cargar = useCallback((conSpinner = true) => {
        if (conSpinner) setLoading(true);
        setError(null);

        dashboardServices
            .getActiveRoutes({fecha: dia})
            .then((res) => setData(res))
            .catch(() => setError("No se pudieron cargar las rutas en curso."))
            .finally(() => setLoading(false));
    }, [dia]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    // El refresco no muestra el spinner: la tabla se actualiza debajo sin parpadear, que
    // es lo que uno espera de una pantalla que se mira de reojo.
    useEffect(() => {
        const id = setInterval(() => cargar(false), REFRESCO_MS);
        return () => clearInterval(id);
    }, [cargar]);

    const filas = data?.rows || [];
    const totales = data?.totals;

    return (
        <Card sx={{mb: 3}}>
            <CardContent>
                <Stack
                    direction={{xs: "column", md: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", md: "center"}}
                    spacing={1.5}
                    sx={{mb: 2}}
                >
                    <Stack spacing={0.5}>
                        <Typography variant="h4" sx={{mb: 0}}>Rutas iniciadas</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pedidos de los viajes que el conductor arrancó ese día.
                            {data?.generated_at && ` Actualizado ${moment(data.generated_at).format("HH:mm")}.`}
                        </Typography>
                    </Stack>

                    <TextField
                        id="active-routes-dia"
                        label="Día"
                        type="date"
                        size="small"
                        value={dia}
                        onChange={(e) => setDia(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                </Stack>

                {loading && (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress/>
                    </Box>
                )}

                {!loading && error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && filas.length === 0 && (
                    <Alert severity="info">Ningún conductor arrancó una ruta en este día.</Alert>
                )}

                {!loading && !error && filas.length > 0 && (
                    <>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mb: 2}}>
                            <Chip
                                size="small"
                                label={`${totales.trips} ${totales.trips === 1 ? "ruta iniciada" : "rutas iniciadas"}`}
                                sx={{fontWeight: 600}}
                            />
                            <Chip
                                size="small"
                                label={`${totales.deliveries} ${totales.deliveries === 1 ? "pedido" : "pedidos"}`}
                                sx={{fontWeight: 600}}
                            />
                            {totales.over_workday > 0 && (
                                <Chip
                                    size="small"
                                    label={`${totales.over_workday} terminan fuera de jornada`}
                                    sx={{
                                        fontWeight: 600,
                                        color: "#EA580C",
                                        bgcolor: "#EA580C14",
                                        border: "1px solid #EA580C66"
                                    }}
                                />
                            )}
                            {totales.without_plan > 0 && (
                                <Chip
                                    size="small"
                                    label={`${totales.without_plan} sin ruta calculada`}
                                    sx={{fontWeight: 600}}
                                />
                            )}
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Viaje</TableCell>
                                        <TableCell>Pedido</TableCell>
                                        <TableCell>Chofer</TableCell>
                                        <TableCell sx={{minWidth: 150}}>Avance</TableCell>
                                        <TableCell align="right">Cerrada</TableCell>
                                        <TableCell align="right">Termina</TableCell>
                                        <TableCell align="right">Jornada</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filas.map((fila, i) => {
                                        // Primera fila de su viaje: es la unica que repite los
                                        // datos del viaje, y la que lleva la linea divisoria que
                                        // separa un grupo del siguiente.
                                        const abre = i === 0 || filas[i - 1].trip_id !== fila.trip_id;
                                        const avance = fila.trip_deliveries > 0
                                            ? Math.round((fila.trip_closed / fila.trip_deliveries) * 100)
                                            : 0;
                                        const sinBorde = abre ? {} : {borderTop: "none"};

                                        return (
                                            <TableRow
                                                key={fila.delivery_id}
                                                sx={abre && i > 0 ? {"& td": {borderTop: "1px solid rgba(0,0,0,0.12)"}} : {}}
                                            >
                                                <TableCell sx={sinBorde}>{abre ? fila.trip_code : ""}</TableCell>
                                                <TableCell sx={sinBorde}>
                                                    {fila.order_code || (
                                                        <Typography variant="caption" color="text.secondary">
                                                            sin pedido
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={sinBorde}>
                                                    {abre ? (fila.driver || "Sin asignar") : ""}
                                                </TableCell>
                                                <TableCell sx={sinBorde}>
                                                    {abre && (
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={avance}
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    height: 8,
                                                                    borderRadius: 1,
                                                                    bgcolor: `${TONO}22`,
                                                                    "& .MuiLinearProgress-bar": {bgcolor: TONO}
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{whiteSpace: "nowrap"}}>
                                                                {fila.trip_closed}/{fila.trip_deliveries}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={sinBorde}>
                                                    {hora(fila.closed_at, dia) || "—"}
                                                </TableCell>
                                                <TableCell align="right" sx={sinBorde}>
                                                    {hora(fila.ends_at, dia) || (
                                                        <Typography variant="caption" color="text.secondary">
                                                            sin ruta calculada
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={sinBorde}>
                                                    {fila.over_workday ? (
                                                        <Chip
                                                            size="small"
                                                            label={`+${fila.over_minutes} min`}
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: "#EA580C",
                                                                bgcolor: "#EA580C14",
                                                                border: "1px solid #EA580C66"
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {hora(fila.workday_end, dia) || "sin jornada"}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default ActiveRoutes;
